import {AsyncButton, ButtonSize} from "@components/Button";
import {ButtonStatus} from "@components/Button/constants";
import {TextInput} from "@components/Input";
import {useFormErrorMessages} from "@components/Input/utils/errorMessages";
import {requireEmail} from "@components/Input/utils/Validators";
import {useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {useSelectedDoctor} from "@page-containers/booking/utils/useSelectedDoctor";
import {getMilesAway} from "@page-containers/booking/utils/webBookingAnalytics";
import {useTranslation} from "@i18n/client";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDisclosure} from "src/hooks/useDisclosure";
import {useGeolocation} from "src/utils/browser-storage/location";
import {v4} from "uuid";

import {MonolithLocationFields} from "../../../../_services/graphql/queries/location";
import {useEmailEnteredHandler} from "../../../../hooks/booking/useEmailEnteredHandler";
import {I18nNamespace} from "@i18n";
import {AUTO_FILL_FORMS} from "../../../_common/_constants";
import {AppointmentTile} from "../components/AppointmentTile";
import Heading from "../Heading";
import {postEmailErrorEvent, postEmailEvent, postViewEvent} from "./events";
import TokenSent from "./TokenSent";

/**
 * Component which renders a tile. The location image is on the left and appointment info
 * on the right.
 */
export type Props = {
  location: MonolithLocationFields;
  reasonId: string;
  reasonSlug: string;
  time: number; // epoch millis
  specialtyId: string;
  dropoutVisitorId?: string;
};

const EnterEmailPage: React.FC<Props> = ({
  location,
  reasonId,
  reasonSlug,
  time,
  specialtyId,
  dropoutVisitorId,
}) => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const [viewId] = useState(v4());
  const [tokenSentTo, setTokenSentTo] = useState<string | null>(null);

  /**
   * Handles loading state when submitted via enter key on form.
   */
  const formSubmitStatus = useDisclosure();

  const [email, setEmail] = useState("");
  const {invalidEmail} = useFormErrorMessages();

  const {selectedSlotIds, selectedTime} = useParsedBookingQuery();

  const userPosition = useGeolocation();

  const emailEnteredHandler = useEmailEnteredHandler(
    viewId,
    reasonId,
    reasonSlug,
    location,
    time,
    dropoutVisitorId,
  );

  const emailValidator = useCallback(requireEmail(invalidEmail), [requireEmail, i18n]);
  const isEmailValid = typeof emailValidator(email) !== "string";

  const [validateEmail, setValidateEmail] = useState<boolean>(false);
  const errorMsg = useMemo(() => {
    return validateEmail ? emailValidator(email) : undefined;
  }, [email, emailValidator, validateEmail]);

  const onNext = useCallback(async () => {
    if (isEmailValid) {
      const tokenDestinationEmail = await emailEnteredHandler(email);
      setTokenSentTo(tokenDestinationEmail);
    }
  }, [emailEnteredHandler, isEmailValid, email]);

  const emitEmailEvent = useCallback(() => {
    if (email && isEmailValid) postEmailEvent({viewId, value: email});
  }, [email, isEmailValid, viewId]);

  const handleSetValidateEmail = useCallback(
    (val: boolean) => {
      setValidateEmail(val);

      const error = errorMsg || emailValidator(email);
      if (error) postEmailErrorEvent({viewId, errorText: error, emailText: email});
    },
    [email, emailValidator, errorMsg, viewId],
  );

  const handleOnEnterPress = useCallback(async () => {
    if (formSubmitStatus.isClosed) {
      formSubmitStatus.open();
      emitEmailEvent();
      try {
        await onNext();
      } finally {
        formSubmitStatus.close();
      }
    }
  }, [emitEmailEvent, formSubmitStatus, onNext]);

  const emitViewEvent = useCallback(() => {
    postViewEvent({
      viewId,
      apptReasonId: reasonId,
      locationId: location.id,
      slotId: selectedSlotIds?.[0],
      milesAway: userPosition && location ? getMilesAway(userPosition, location) : undefined,
      timeToAppt: selectedTime ? selectedTime - Date.now() : undefined,
      specialtyId,
    });
  }, [location, reasonId, selectedSlotIds, selectedTime, userPosition, viewId]);

  useEffect(() => {
    if (userPosition) emitViewEvent();
  }, [emitViewEvent, userPosition]);

  const doctor = useSelectedDoctor();

  if (tokenSentTo) {
    return <TokenSent email={tokenSentTo} />;
  }

  return (
    <div className="flex justify-center">
      <div className="flex flex-col items-center w-[600px]">
        <AppointmentTile
          firstName={doctor?.firstName || undefined}
          lastName={doctor?.lastName || undefined}
          credential={doctor?.credential || undefined}
          imageId={doctor?.coverImage?.id || location.coverImageId}
          locationTimezone={location.timezone}
          street={location.address.firstLine}
          city={location.address.city}
          time={time}
        />
        <Heading className="mt-12">{i18n.t("What is your email?", {ns: "web-scheduling"})}</Heading>
        <p className="mt-4 mb-8 max-w-[25rem] text-center">
          {i18n.t(
            "We'll use this to set up a new account or link this appointment to an existing account if you have one.",
            {ns: "web-scheduling"},
          )}
        </p>
        <TextInput
          ariaLabel={i18n.t("Email address", {ns: "web-scheduling"})}
          className="w-full"
          onChange={setEmail}
          onEnter={handleOnEnterPress}
          placeholder={i18n.t("yourname@example.com")}
          label={i18n.t("Email Address", {ns: "web-scheduling"})}
          errorMsg={errorMsg}
          setValidation={handleSetValidateEmail}
          onBlur={emitEmailEvent}
          defaultValue={AUTO_FILL_FORMS ? `devbooking+${Date.now()}@carbonhealth.co` : undefined}
          data-cy="email"
          inputMode="email"
        />
        <AsyncButton
          label={i18n.t("Next", {ns: "web-scheduling"})}
          loadingLabel={i18n.t("Loading", {ns: "web-scheduling"})}
          successLabel={i18n.t("Success", {ns: "web-scheduling"})}
          size={ButtonSize.LG}
          onClick={onNext}
          className="w-full mt-8"
          statusOverride={formSubmitStatus.isOpen ? ButtonStatus.LOADING : undefined}
        />
      </div>
    </div>
  );
};

export default EnterEmailPage;
